// w14 Diyora — preview thumbnail for the user-imported standalone HTML
// template. The full design lives at /templates/diyora-shahzod.html and
// is rendered byte-for-byte by the matching variant — this layout only
// has to make a tasteful 5:7 card so the gallery/landing show something
// in the cream + gold palette of the source design.

import type { LayoutCtx } from "./_shared";

export function DiyoraPreviewLayout({ names, date, venue, accent, ink, bg, font }: LayoutCtx) {
  // Visually match the source: cream paper, gold rules, Italiana-leaning
  // serif for the names. We approximate with whatever font the template
  // ships ("Cormorant Garamond"); the real Italiana shows up in the
  // variant's iframe.
  return (
    <div
      style={{
        position: "absolute",
        inset: 0,
        background: bg,
        color: ink,
        overflow: "hidden",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        textAlign: "center",
        padding: "10% 8%",
      }}
    >
      {/* Outer hairline gold frame — the source design uses one too */}
      <div
        aria-hidden
        style={{ position: "absolute", inset: "5%", border: `1px solid ${accent}55`, pointerEvents: "none" }}
      />
      <div
        aria-hidden
        style={{ position: "absolute", inset: "6.5%", border: `0.5px solid ${ink}10`, pointerEvents: "none" }}
      />

      {/* Eyebrow */}
      <div
        style={{
          fontFamily: "var(--font-ui)",
          fontSize: 7,
          fontWeight: 700,
          letterSpacing: "0.42em",
          color: accent,
          marginBottom: 14,
          display: "flex",
          alignItems: "center",
          gap: 8,
        }}
      >
        <span style={{ width: 14, height: 1, background: accent }} />
        TAKLIFNOMA
        <span style={{ width: 14, height: 1, background: accent }} />
      </div>

      {/* Names — split across two lines, the way the source hero does */}
      <div
        style={{
          fontFamily: font,
          fontStyle: "italic",
          fontWeight: 400,
          fontSize: 32,
          lineHeight: 1.0,
          color: ink,
          letterSpacing: "0.01em",
        }}
      >
        {names.split(/\s*&\s*/)[0] ?? names}
      </div>
      <div
        style={{
          fontFamily: "'Pinyon Script', cursive",
          fontSize: 28,
          color: accent,
          margin: "6px 0",
          lineHeight: 1,
        }}
      >
        &
      </div>
      <div
        style={{
          fontFamily: font,
          fontStyle: "italic",
          fontWeight: 400,
          fontSize: 32,
          lineHeight: 1.0,
          color: ink,
        }}
      >
        {names.split(/\s*&\s*/)[1] ?? ""}
      </div>

      {/* Hairline divider */}
      <div
        aria-hidden
        style={{
          width: "44%",
          height: 1,
          background: accent,
          opacity: 0.6,
          margin: "20px 0 14px",
        }}
      />

      {/* Date · venue */}
      <div
        style={{
          fontFamily: "var(--font-ui)",
          fontSize: 7.5,
          fontWeight: 600,
          letterSpacing: "0.34em",
          color: ink,
          fontFeatureSettings: '"tnum" 1, "lnum" 1',
        }}
      >
        {date.replace(/\./g, " · ")}
      </div>
      <div
        style={{
          fontFamily: font,
          fontStyle: "italic",
          fontSize: 12,
          color: ink,
          opacity: 0.78,
          marginTop: 6,
        }}
      >
        {venue}
      </div>
    </div>
  );
}
